import { useState, useEffect, useCallback, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../store/store';
import { fetchDiscoveryProfiles, updateFilters as updateDiscoveryFilters, nextPage as goNextPage, prevPage as goPrevPage, goToPage as goToPageAction } from '../store/slices/discoverySlice';

interface ProfileFilters {
  page?: number;
  limit?: number;
  gender?: string;
  minAge?: number;
  maxAge?: number;
  religion?: string;
  community?: string;
  caste?: string;
  motherTongue?: string;
  city?: string;
  state?: string;
  country?: string;
  maritalStatus?: string;
  education?: string;
  educationLevel?: string;
  occupation?: string;
  annualIncome?: string;
  minHeight?: number;
  maxHeight?: number;
  search?: string;
  filterType?: 'all' | 'new' | 'premium' | 'nearby' | 'verified' | 'recently-active';
}

export const useProfiles = (initialFilters: ProfileFilters = {}) => {
  const dispatch = useAppDispatch();
  const { profiles, loading, backgroundLoading, error, pagination, filters: currentFilters, lastFetched, cacheExpiry } = useAppSelector(state => state.discovery);
  const hasInitialized = useRef(false);
  const currentFiltersRef = useRef(currentFilters);
  const isFetchingMore = useRef(false);

  // Safety check for pagination
  const safePagination = pagination || { page: 1, limit: 20, total: 0, pages: 0 };
  const safeProfiles = profiles || [];

  // Update ref when filters change
  useEffect(() => {
    currentFiltersRef.current = currentFilters;
  }, [currentFilters]);

  // Check if cache is valid - IMPROVED: More generous cache validation
  const isCacheValid = lastFetched > 0 && (Date.now() - lastFetched) < cacheExpiry && profiles.length > 0;

  // Auto-fetch more profiles when list is running low
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !hasInitialized.current || isFetchingMore.current) return;

    // If we have less than 5 profiles and there are more pages available
    const needsMoreProfiles = profiles.length < 5 && pagination.page < pagination.pages;
    
    if (needsMoreProfiles && !loading && !backgroundLoading) {
      isFetchingMore.current = true;
      
      // Fetch next page
      dispatch(goNextPage());
      setTimeout(() => {
        void dispatch(fetchDiscoveryProfiles({ 
          filters: { ...currentFiltersRef.current, page: (currentFiltersRef.current.page || 1) + 1 }, 
          isBackground: true 
        })).finally(() => {
          isFetchingMore.current = false;
        });
      }, 0);
    }
  }, [profiles.length, pagination.page, pagination.pages, loading, backgroundLoading, dispatch]);

  // Initialize and fetch data - FIXED: Better cache handling to prevent refetch on navigation back
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Only initialize once per component mount
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      
      // Set initial filters if provided
      if (Object.keys(initialFilters).length > 0) {
        dispatch(updateDiscoveryFilters(initialFilters));
      }
      
      // Always fetch fresh data on mount to show all profiles without filters
      const finalFilters = Object.keys(initialFilters).length > 0 ? initialFilters : currentFiltersRef.current;
      void dispatch(fetchDiscoveryProfiles({ filters: finalFilters, isBackground: false }));
    }
  }, []);

  const updateFilters = useCallback((newFilters: Partial<ProfileFilters>) => {
    // Development logging
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 useProfiles.updateFilters called with:', newFilters);
      console.log('   - Current filters:', currentFiltersRef.current);
    }
    
    dispatch(updateDiscoveryFilters(newFilters));
    // Fetch with new filters
    setTimeout(() => {
      // Only reset to page 1 if we're not explicitly setting a page
      const finalFilters = newFilters.page !== undefined 
        ? { ...currentFiltersRef.current, ...newFilters }
        : { ...currentFiltersRef.current, ...newFilters, page: 1 };
      
      if (process.env.NODE_ENV === 'development') {
        console.log('   - Final filters being sent to API:', finalFilters);
      }
      
      void dispatch(fetchDiscoveryProfiles({ filters: finalFilters, isBackground: false }));
    }, 0);
  }, [dispatch]);

  const resetFilters = useCallback(() => {
    // Development logging
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 useProfiles.resetFilters called');
    }
    
    // Import the resetAllFilters action
    import('../store/slices/discoverySlice').then(({ resetAllFilters }) => {
      dispatch(resetAllFilters());
      // Fetch all profiles without filters
      setTimeout(() => {
        void dispatch(fetchDiscoveryProfiles({ filters: { page: 1, limit: 20 }, isBackground: false }));
      }, 0);
    });
  }, [dispatch]);

  const nextPage = useCallback(() => {
    dispatch(goNextPage());
    setTimeout(() => {
      void dispatch(fetchDiscoveryProfiles({ filters: currentFiltersRef.current, isBackground: false }));
    }, 0);
  }, [dispatch]);

  const prevPage = useCallback(() => {
    dispatch(goPrevPage());
    setTimeout(() => {
      void dispatch(fetchDiscoveryProfiles({ filters: currentFiltersRef.current, isBackground: false }));
    }, 0);
  }, [dispatch]);

  const goToPage = useCallback((page: number) => {
    dispatch(goToPageAction(page));
    setTimeout(() => {
      void dispatch(fetchDiscoveryProfiles({ filters: currentFiltersRef.current, isBackground: false }));
    }, 0);
  }, [dispatch]);

  const refetch = useCallback(() => {
    void dispatch(fetchDiscoveryProfiles({ filters: currentFiltersRef.current, isBackground: false }));
  }, [dispatch]);

  return {
    profiles: safeProfiles,
    loading: loading && !isCacheValid,
    error,
    pagination: safePagination,
    filters: currentFilters,
    updateFilters,
    resetFilters,
    nextPage,
    prevPage,
    goToPage,
    refetch
  };
};